import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  BarChart3,
  Users,
  Building2,
  CalendarClock,
  AlertTriangle,
  Clock,
  Coins,
  Hourglass,
  Info,
  Landmark,
  ListChecks,
  Receipt,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import { Switch } from '../../../components/ui/Switch';
import { Tooltip } from '../../../components/ui/Tooltip';
import {
  DepartmentFilter,
  EMPTY_VALUE,
  ExportMenu,
  PeriodPicker,
  ReportEmpty,
  ReportFilterBar,
  ReportFilterField,
  ReportIdle,
  ReportLoading,
  ReportSummaryStrip,
  ReportTable,
  RunButton,
  StatusChip,
  buildExportRequest,
  dayStatusLabelKey,
  dayStatusTone,
  formatCountWithMinutes,
  formatDayShort,
  formatDecimal,
  formatHours,
  formatInt,
  formatMoney,
  formatMonth,
  formatPercent,
  formatPeriod,
  otApprovalLabelKey,
  otApprovalTone,
  reportExportFilename,
  reportExportLabels,
  resolvePeriodPreset,
  useGroupCollapse,
  useReportExport,
} from '../../../components/reports';
import type {
  GroupCollapseApi,
  PeriodValue,
  ReportColumn,
  ReportExportFormat,
  ReportGroup,
  ReportSummaryItem,
} from '../../../components/reports';
import { postReportExport } from '../../../api/reports';
import {
  getPayrollSummary,
  getSalaryRegister,
  getDepartmentCost,
  getAttendanceOvertimeDetail,
  PayrollReportInput,
  PayrollSummaryView,
  SalaryRegisterView,
  DepartmentCostView,
  AttendanceOvertimeDayView,
  AttendanceOvertimeDetailView,
} from '../../../api/Payroll/payrollReports';
import { getDepartments } from '../../../api/General/Company';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../../../utils/apiErrors';

type ReportTab = 'summary' | 'register' | 'department' | 'attendance';

interface DepartmentOption {
  id: number;
  name: string;
}

/** One day row of the attendance table: the server's day plus the group's currency, which the
 *  money column needs per ROW (`currencyOf`) rather than per group. */
interface AttendanceDayRow extends AttendanceOvertimeDayView {
  rowId: string;
  currencyCode: string;
}

/** A day nobody was expected to work reads as context, not as data. */
const MUTED_DAY_STATUSES = new Set(['Future', 'WeekOff', 'Holiday']);

const DAY_STATUSES = [
  'Present', 'Late', 'Absent', 'Leave', 'HalfDayLeave',
  'SickLeave', 'Holiday', 'WeekOff', 'Unscheduled', 'Future',
];
const OT_APPROVAL_STATUSES = ['NotRequired', 'Pending', 'Approved', 'Rejected', 'Mixed'];

/** The raw name `PayrollReportsService` gives an employee with no department. It is a server value,
 *  not a label: the screen and the export both translate it. */
const UNASSIGNED_DEPARTMENT = 'Unassigned';

/** How many skeleton columns each tab's loading state should mimic, so the shape does not jump. */
const SKELETON_COLUMNS: Record<ReportTab, number> = {
  summary: 7,
  register: 9,
  department: 9,
  attendance: 9,
};

/** Payable + rejected: a rejected day is still a day the report is being read for. */
function dayHasOvertime(d: AttendanceOvertimeDayView): boolean {
  return d.regularOvertimeHours + d.premiumOvertimeHours + d.rejectedOvertimeHours > 0;
}

/** Hours on days whose overtime decision is still open (chip reads Pending / Mixed). */
function undecidedHours(emp: AttendanceOvertimeDetailView): number {
  return emp.days.reduce((a, d) => (
    d.overtimeApprovalStatus === 'Pending' || d.overtimeApprovalStatus === 'Mixed'
      ? a + d.regularOvertimeHours + d.premiumOvertimeHours
      : a), 0);
}

/* ── shared shaping helpers ─────────────────────────────────────────────────────────────────────
 * Module level on purpose: they take `lang`/`t` as arguments rather than closing over them, so the
 * memos that call them keep small, honest dependency lists.
 */

interface MonthRow { month: string }
interface MoneyRow { currencyCode: string }

/** Rows bucketed by a key, in the order the buckets first appear, then sorted by the key. */
function groupBy<Row>(rows: Row[], keyOf: (row: Row) => string): [string, Row[]][] {
  const map = new Map<string, Row[]>();
  for (const row of rows) {
    const bucket = map.get(keyOf(row));
    if (bucket) bucket.push(row);
    else map.set(keyOf(row), [row]);
  }
  return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
}

/** The months present, ascending. `yyyy-MM` sorts correctly as a string. */
function monthsOf(rows: MonthRow[]): string[] {
  return Array.from(new Set(rows.map((r) => r.month))).sort();
}

/**
 * Money is only meaningful inside ONE currency, so every money figure on a strip is produced per
 * code. A tenant with one currency sees one line; a mixed one sees a line each, never a single
 * number that is the sum of two different things.
 */
function sumByCurrency<Row extends MoneyRow>(rows: Row[], pick: (row: Row) => number): Map<string, number> {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const code = row.currencyCode || '';
    totals.set(code, (totals.get(code) ?? 0) + pick(row));
  }
  return totals;
}

interface CurrencyItemsOptions<Row extends MoneyRow> {
  rows: Row[];
  pick: (row: Row) => number;
  /** Plain label for a single-currency tenant. */
  label: string;
  /** Label carrying the code, used only when the report really spans several currencies. */
  labelIn: (currency: string) => string;
  icon: LucideIcon;
  /** The first line always keeps the plain id; extra currencies are named by their code. */
  testIdBase: string;
  lang: string;
  decorate?: (currency: string) => Partial<ReportSummaryItem>;
}

function currencyItems<Row extends MoneyRow>({
  rows, pick, label, labelIn, icon, testIdBase, lang, decorate,
}: CurrencyItemsOptions<Row>): ReportSummaryItem[] {
  const totals = sumByCurrency(rows, pick);
  const codes = Array.from(totals.keys()).sort();
  const mixed = codes.length > 1;
  return codes.map((code, index) => ({
    label: mixed && code ? labelIn(code) : label,
    value: formatMoney(totals.get(code) ?? 0, code, lang),
    icon,
    testId: index === 0 ? testIdBase : `${testIdBase}-${code}`,
    ...decorate?.(code),
  }));
}

/**
 * The latest month against the one before it — the only comparison a multi-month payroll report can
 * honestly make from its own rows. Returns null when there is nothing to compare against, so the
 * caller renders no hint at all rather than a `0%` that reads as "unchanged".
 */
function monthOverMonth<Row extends MonthRow & MoneyRow>(
  rows: Row[],
  pick: (row: Row) => number,
  currency: string,
): { delta: number; previousMonth: string } | null {
  const months = monthsOf(rows);
  if (months.length < 2) return null;
  const previousMonth = months[months.length - 2];
  const latestMonth = months[months.length - 1];
  const sumIn = (month: string) => rows
    .filter((r) => r.month === month && (r.currencyCode || '') === currency)
    .reduce((a, r) => a + pick(r), 0);
  const before = sumIn(previousMonth);
  if (before === 0) return null;
  return { delta: (sumIn(latestMonth) - before) / Math.abs(before), previousMonth };
}

export default function PayrollReports() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const company = useSelector((state: { user?: { company?: { id?: number; name?: string } } }) => state.user?.company);
  const [activeTab, setActiveTab] = useState<ReportTab>('summary');
  const [loading, setLoading] = useState(false);
  // Month selectors, LOCAL calendar months. The default is last month, which is the month HR
  // actually reports on — the current one is still being worked.
  const [period, setPeriod] = useState<PeriodValue>(
    () => resolvePeriodPreset('lastMonth', 'month') ?? { from: '', to: '' },
  );

  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<number[]>([]);

  const [summaryData, setSummaryData] = useState<PayrollSummaryView[]>([]);
  const [registerData, setRegisterData] = useState<SalaryRegisterView[]>([]);
  const [departmentData, setDepartmentData] = useState<DepartmentCostView[]>([]);
  const [attendanceData, setAttendanceData] = useState<AttendanceOvertimeDetailView[]>([]);
  const [onlyOvertimeDays, setOnlyOvertimeDays] = useState(false);

  // Which tabs hold data for the CURRENT filter set. Changing any filter empties this, so stale
  // numbers can never sit under a new date range; switching to an unloaded tab refetches on its own.
  const [loadedTabs, setLoadedTabs] = useState<Set<ReportTab>>(() => new Set());
  // Every tab now exports the same way: the SERVER re-runs the filter and renders the document, so
  // one busy state covers the whole page.
  const { busy: exporting, exportReport } = useReportExport();
  const requestSeq = useRef(0);

  const invalidRange = !!period.from && !!period.to && period.from > period.to;

  useEffect(() => {
    const companyId = company?.id;
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getDepartments(companyId);
        if (cancelled) return;
        if (res.errors.length === 0) {
          const list: DepartmentOption[] = (res.payload ?? []).map((d) => ({ id: d.id, name: d.name }));
          list.sort((a, b) => a.name.localeCompare(b.name));
          setDepartments(list);
        }
      } catch {
        /* the filter is optional; the report still runs company-wide */
      }
    })();
    return () => { cancelled = true; };
  }, [company?.id]);

  const buildInput = useCallback((): PayrollReportInput => ({
    fromDate: `${period.from}-01`,
    toDate: `${period.to}-01`,
    departmentIds: selectedDepartmentIds.length > 0 ? selectedDepartmentIds : undefined,
  }), [period.from, period.to, selectedDepartmentIds]);

  const fetchTab = useCallback(async (tab: ReportTab) => {
    if (invalidRange) {
      toast.error(t('payrollReports.invalidRange'));
      return;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const input = buildInput();
      const fail = (r: { errors: unknown[] }) => toast.error(getApiErrorMessage(r, t, t('payrollReports.error')));
      let ok = false;
      if (tab === 'summary') {
        const r = await getPayrollSummary(input);
        if (seq !== requestSeq.current) return;
        if (r.errors.length === 0) { setSummaryData(r.payload ?? []); ok = true; } else fail(r);
      } else if (tab === 'register') {
        const r = await getSalaryRegister(input);
        if (seq !== requestSeq.current) return;
        if (r.errors.length === 0) { setRegisterData(r.payload ?? []); ok = true; } else fail(r);
      } else if (tab === 'department') {
        const r = await getDepartmentCost(input);
        if (seq !== requestSeq.current) return;
        if (r.errors.length === 0) { setDepartmentData(r.payload ?? []); ok = true; } else fail(r);
      } else {
        const r = await getAttendanceOvertimeDetail(input);
        if (seq !== requestSeq.current) return;
        if (r.errors.length === 0) { setAttendanceData(r.payload ?? []); ok = true; } else fail(r);
      }
      if (ok) setLoadedTabs((prev) => new Set(prev).add(tab));
    } catch (err) {
      if (seq === requestSeq.current) toast.error(getApiErrorMessage(err, t, t('payrollReports.error')));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [buildInput, invalidRange, t]);

  // Any filter change invalidates every loaded tab.
  useEffect(() => {
    setLoadedTabs(new Set());
  }, [period.from, period.to, selectedDepartmentIds]);

  const anyLoaded = loadedTabs.size > 0;
  const hasLoaded = loadedTabs.has(activeTab);

  const switchTab = (tab: ReportTab) => {
    setActiveTab(tab);
    if (anyLoaded && !loadedTabs.has(tab) && !loading) {
      void fetchTab(tab);
    }
  };

  const tabs: { key: ReportTab; label: string; icon: React.ElementType }[] = [
    { key: 'summary', label: t('payrollReports.summary'), icon: BarChart3 },
    { key: 'register', label: t('payrollReports.salaryRegister'), icon: Users },
    { key: 'department', label: t('payrollReports.departmentCost'), icon: Building2 },
    { key: 'attendance', label: t('payrollReports.attendanceOvertime'), icon: CalendarClock },
  ];

  const unassignedLabel = t('payrollReports.unassigned');
  const deptName = useCallback(
    (name: string) => (name === UNASSIGNED_DEPARTMENT ? unassignedLabel : name),
    [unassignedLabel],
  );

  /**
   * A money cell on screen.
   *
   * Zero is context, not a figure: a register where every deduction, adjustment and overtime cell
   * prints `0.00 AED` buries the handful of rows that carry one. The dash is muted so the eye skips
   * it; the raw 0 still reaches the totals and the export, which is why the value function is
   * untouched and only the RENDER changes. Same rule the attendance tab's overtime columns use.
   */
  const money = useCallback((value: number | null | undefined, currency: string) => (
    value
      ? formatMoney(value, currency, lang)
      : <span className="font-normal text-muted-foreground/60">{EMPTY_VALUE}</span>
  ), [lang]);

  /** What the figures on screen were produced with — printed on the export and echoed in the chips. */
  const periodText = formatPeriod(period.from, period.to, lang);
  const scopeText = selectedDepartmentIds.length === 0
    ? t('reports.allDepartments')
    : selectedDepartmentIds
      .map((id) => (id === 0 ? unassignedLabel : departments.find((d) => d.id === id)?.name ?? String(id)))
      .join(', ');

  // ── column specs ──────────────────────────────────────────────────────────────────────────────
  //
  // One spec per tab drives the table body, the group subtotals, the grand total AND the export
  // request. The `key` of each column is the CONTRACT the server export reads
  // (`PayrollReportsController.Export/{PayrollSummary|SalaryRegister|DepartmentCost}` writes its row
  // dictionary under exactly these names), so renaming one here silently blanks a column in the file.

  const summaryColumns: ReportColumn<PayrollSummaryView>[] = useMemo(() => [
    {
      key: 'department', label: t('payrollReports.department'), kind: 'text', width: '15rem',
      value: (r) => deptName(r.departmentName),
    },
    { key: 'headcount', label: t('payrollReports.headcount'), kind: 'int', total: 'sum', value: (r) => r.headcount },
    {
      key: 'gross', label: t('payrollReports.grossTotal'), kind: 'money', total: 'sum',
      value: (r) => r.grossTotal, render: (r) => money(r.grossTotal, r.currencyCode),
    },
    {
      key: 'deductions', label: t('payrollReports.deductionsTotal'), kind: 'money', total: 'sum',
      value: (r) => r.deductionsTotal, render: (r) => money(r.deductionsTotal, r.currencyCode),
    },
    {
      key: 'variable', label: t('payrollReports.variablePayTotal'), kind: 'money', total: 'sum',
      value: (r) => r.variablePayTotal, render: (r) => money(r.variablePayTotal, r.currencyCode),
    },
    {
      key: 'otPay', label: t('payrollReports.overtimePay'), kind: 'money', total: 'sum',
      value: (r) => r.overtimeTotal, render: (r) => money(r.overtimeTotal, r.currencyCode),
    },
    {
      key: 'net', label: t('payrollReports.netTotal'), kind: 'money', total: 'sum', emphasis: true,
      value: (r) => r.netTotal, render: (r) => money(r.netTotal, r.currencyCode),
    },
  ], [t, deptName, money]);

  // The month column is dead weight on a single-month run — every row would repeat the same word,
  // and the reader already sees the month on the filter chip. It appears the moment the range spans
  // more than one, and the export column list follows the screen because both read this array.
  const registerSpansMonths = period.from !== period.to;

  const registerColumns: ReportColumn<SalaryRegisterView>[] = useMemo(() => [
    {
      key: 'employee', label: t('payrollReports.employee'), kind: 'text', width: '16rem', emphasis: true,
      value: (r) => r.employeeName,
    },
    ...(registerSpansMonths
      ? [{
        key: 'month', label: t('payrollReports.month'), kind: 'month' as const, muted: true,
        value: (r: SalaryRegisterView) => r.month,
        render: (r: SalaryRegisterView) => formatMonth(r.month, lang),
      }]
      : []),
    {
      key: 'gross', label: t('payrollReports.grossSalary'), kind: 'money', total: 'sum',
      value: (r) => r.grossSalary, render: (r) => money(r.grossSalary, r.currencyCode),
    },
    {
      key: 'deductions', label: t('payrollReports.deductionsTotal'), kind: 'money', total: 'sum',
      value: (r) => r.deductions, render: (r) => money(r.deductions, r.currencyCode),
    },
    {
      key: 'variable', label: t('payrollReports.variablePayTotal'), kind: 'money', total: 'sum',
      value: (r) => r.variablePay, render: (r) => money(r.variablePay, r.currencyCode),
    },
    {
      key: 'otHours', label: t('payrollReports.overtimeHours'), kind: 'hours', total: 'sum',
      value: (r) => r.overtimeHours,
      render: (r) => (r.overtimeHours > 0
        ? formatHours(r.overtimeHours)
        : <span className="text-muted-foreground/60">{EMPTY_VALUE}</span>),
    },
    {
      key: 'otPay', label: t('payrollReports.overtimePay'), kind: 'money', total: 'sum',
      value: (r) => r.overtimePay ?? 0, render: (r) => money(r.overtimePay, r.currencyCode),
    },
    {
      key: 'adjustments', label: t('payrollReports.adjustments'), kind: 'money', total: 'sum',
      value: (r) => r.adjustments, render: (r) => money(r.adjustments, r.currencyCode),
    },
    {
      key: 'net', label: t('payrollReports.netPay'), kind: 'money', total: 'sum', emphasis: true,
      value: (r) => r.netPay, render: (r) => money(r.netPay, r.currencyCode),
    },
  ], [t, lang, money, registerSpansMonths]);

  const departmentColumns: ReportColumn<DepartmentCostView>[] = useMemo(() => [
    {
      key: 'department', label: t('payrollReports.department'), kind: 'text', width: '15rem',
      value: (r) => deptName(r.departmentName),
    },
    { key: 'headcount', label: t('payrollReports.headcount'), kind: 'int', total: 'sum', value: (r) => r.headcount },
    {
      key: 'gross', label: t('payrollReports.grossTotal'), kind: 'money', total: 'sum',
      value: (r) => r.grossTotal, render: (r) => money(r.grossTotal, r.currencyCode),
    },
    {
      key: 'additional', label: t('payrollReports.additionalEarnings'), kind: 'money', total: 'sum',
      value: (r) => r.additionalEarnings, render: (r) => money(r.additionalEarnings, r.currencyCode),
    },
    {
      key: 'employer', label: t('payrollReports.employerContributions'), kind: 'money', total: 'sum',
      value: (r) => r.employerContributions, render: (r) => money(r.employerContributions, r.currencyCode),
    },
    {
      key: 'cost', label: t('payrollReports.costToCompany'), kind: 'money', total: 'sum', emphasis: true,
      value: (r) => r.totalCost, render: (r) => money(r.totalCost, r.currencyCode),
    },
    {
      key: 'net', label: t('payrollReports.netTotal'), kind: 'money', total: 'sum',
      value: (r) => r.netTotal, render: (r) => money(r.netTotal, r.currencyCode),
    },
    // Averages are per-employee ratios: summing them across departments produces a number that is
    // not an average of anything, so they carry no total and stay muted beside the figures that do.
    {
      key: 'avgGross', label: t('payrollReports.avgGrossSalary'), kind: 'money', total: 'none', muted: true,
      value: (r) => r.avgGrossSalary, render: (r) => money(r.avgGrossSalary, r.currencyCode),
    },
    {
      key: 'avgCost', label: t('payrollReports.avgCostPerEmployee'), kind: 'money', total: 'none', muted: true,
      value: (r) => r.avgCostPerEmployee, render: (r) => money(r.avgCostPerEmployee, r.currencyCode),
    },
  ], [t, deptName, money]);

  /**
   * Attendance & Overtime, one row per DAY.
   *
   * The keys are the contract the server export reads (`PayrollReportsController.ExportAttendanceOvertime`
   * writes its row dictionary under exactly these names), so a rename here silently blanks a column in
   * the file. `date` keeps kind `date` — the custom render is only the short form the grouped table
   * wants, while the export still receives a real date to write as a date.
   */
  const attendanceColumns: ReportColumn<AttendanceDayRow>[] = useMemo(() => [
    {
      key: 'date',
      label: t('payrollReports.day'),
      kind: 'date',
      width: '10rem',
      value: (r) => r.date,
      render: (r) => formatDayShort(r.date, lang),
    },
    {
      key: 'status',
      label: t('payrollReports.dayStatus'),
      kind: 'status',
      value: (r) => r.dayStatus,
      render: (r) => {
        const key = dayStatusLabelKey(r.dayStatus);
        return <StatusChip tone={dayStatusTone(r.dayStatus)}>{key ? t(key) : r.dayStatus}</StatusChip>;
      },
    },
    { key: 'clockIn', label: t('payrollReports.clockIn'), kind: 'text', muted: true, value: (r) => r.clockIn ?? '' },
    { key: 'clockOut', label: t('payrollReports.clockOut'), kind: 'text', muted: true, value: (r) => r.clockOut ?? '' },
    { key: 'worked', label: t('payrollReports.workedHours'), kind: 'hours', total: 'sum', value: (r) => r.workedHours },
    // Zero overtime is the common case on a month of days; printing "0h" / "0.00 AED" on every one of
    // them buries the few days that matter. Zero renders as a muted dash on screen; the export keeps
    // the real 0 so the columns still sum.
    {
      key: 'regularOt', label: t('payrollReports.regularOtHours'), kind: 'hours', total: 'sum',
      value: (r) => r.regularOvertimeHours,
      render: (r) => (r.regularOvertimeHours > 0 ? formatHours(r.regularOvertimeHours) : <span className="text-muted-foreground/60">{EMPTY_VALUE}</span>),
    },
    {
      key: 'premiumOt', label: t('payrollReports.premiumOtHours'), kind: 'hours', total: 'sum',
      value: (r) => r.premiumOvertimeHours,
      render: (r) => (r.premiumOvertimeHours > 0 ? formatHours(r.premiumOvertimeHours) : <span className="text-muted-foreground/60">{EMPTY_VALUE}</span>),
    },
    {
      key: 'otCost',
      label: t('payrollReports.otCost'),
      kind: 'money',
      total: 'sum',
      emphasis: true,
      tooltip: t('payrollReports.otCostHint'),
      value: (r) => r.overtimeCost,
      render: (r) => money(r.overtimeCost, r.currencyCode),
    },
    {
      key: 'approval',
      label: t('payrollReports.approval'),
      kind: 'status',
      hideBelow: 'md',
      value: (r) => r.overtimeApprovalStatus,
      render: (r) => {
        // A day with no overtime has nothing to approve; a chip on every row is noise.
        const hasOt = r.regularOvertimeHours + r.premiumOvertimeHours + r.rejectedOvertimeHours + r.pendingApprovalHours > 0;
        if (!hasOt) return <span className="text-muted-foreground/60">{EMPTY_VALUE}</span>;
        const key = otApprovalLabelKey(r.overtimeApprovalStatus);
        return <StatusChip tone={otApprovalTone(r.overtimeApprovalStatus)}>{key ? t(key) : r.overtimeApprovalStatus}</StatusChip>;
      },
    },
  ], [t, lang, money]);

  // ── groups ────────────────────────────────────────────────────────────────────────────────────

  /** Payroll Summary: one group per MONTH, its rows the departments that were paid in it. */
  const summaryGroups = useMemo<ReportGroup<PayrollSummaryView>[]>(
    () => groupBy(summaryData, (r) => r.month).map(([month, rows]) => ({
      key: month,
      title: formatMonth(month, lang),
      subtitle: t('payrollReports.monthScope', {
        count: new Set(rows.map((r) => r.departmentId)).size,
        headcount: formatInt(rows.reduce((a, r) => a + r.headcount, 0), lang),
      }),
      rows: [...rows].sort((a, b) => deptName(a.departmentName).localeCompare(deptName(b.departmentName), lang)),
    })),
    [summaryData, lang, t, deptName],
  );

  /** Salary Register: one group per DEPARTMENT, its rows the employee-months inside it. */
  const registerGroups = useMemo<ReportGroup<SalaryRegisterView>[]>(
    () => groupBy(registerData, (r) => r.departmentName).map(([department, rows]) => ({
      key: department,
      title: deptName(department),
      subtitle: t('payrollReports.employeeScope', {
        count: new Set(rows.map((r) => r.employeeId)).size,
      }),
      rows: [...rows].sort((a, b) =>
        a.employeeName.localeCompare(b.employeeName, lang) || a.month.localeCompare(b.month)),
    })),
    [registerData, lang, t, deptName],
  );

  /** Department Cost: one group per MONTH, so a multi-month run reads as a trend, not a pile. */
  const departmentGroups = useMemo<ReportGroup<DepartmentCostView>[]>(
    () => groupBy(departmentData, (r) => r.month).map(([month, rows]) => ({
      key: month,
      title: formatMonth(month, lang),
      subtitle: t('payrollReports.monthScope', {
        count: new Set(rows.map((r) => r.departmentId)).size,
        headcount: formatInt(rows.reduce((a, r) => a + r.headcount, 0), lang),
      }),
      rows: [...rows].sort((a, b) => deptName(a.departmentName).localeCompare(deptName(b.departmentName), lang)),
    })),
    [departmentData, lang, t, deptName],
  );

  // ── attendance groups, one per employee-month ─────────────────────────────────────────────────

  const attendanceGroups = useMemo<ReportGroup<AttendanceDayRow>[]>(() => attendanceData.map((emp) => {
    const rows: AttendanceDayRow[] = emp.days.map((d) => ({
      ...d,
      currencyCode: emp.currencyCode,
      rowId: `${emp.employeeId}|${emp.month}|${d.date}`,
    }));
    // The payslip is what was PAID; the day rows are a rate-based ESTIMATE. When they disagree the
    // reader has to be told, or they will quote the wrong figure back to the employee.
    const paid = emp.payslipOvertimePay;
    const paidDiffers = paid !== null && Math.abs(paid - emp.overtimeCost) > 0.01;

    return {
      key: `${emp.employeeId}|${emp.month}`,
      title: emp.employeeName,
      // O4: `unscheduledPresentDays` is a SEPARATE segment, never folded into Present — folding it
      // is exactly what the owner decision of 2026-09-02 ruled against, and it would also break the
      // `scheduledDays >= presentDays + absentDays` invariant the split column exists to protect.
      subtitle: [
        deptName(emp.departmentName),
        formatMonth(emp.month, lang),
        t('payrollReports.dayMix', {
          present: formatDecimal(emp.presentDays, 1, lang),
          absent: formatInt(emp.absentDays, lang),
          leave: formatDecimal(emp.leaveDays, 1, lang),
        }),
        ...(emp.unscheduledPresentDays > 0
          // `days`, not `count`: i18next reserves `count` for pluralisation and types it as a
          // number, so a pre-formatted string there does not type-check.
          ? [t('payrollReports.unscheduledMix', { days: formatDecimal(emp.unscheduledPresentDays, 1, lang) })]
          : []),
      ].join(' · '),
      badges: (
        <>
          <StatusChip tone={emp.overtimeSource === 'Payroll' ? 'info' : 'neutral'}>
            {emp.overtimeSource === 'Payroll'
              ? t('payrollReports.sourcePayroll')
              : t('payrollReports.sourceAttendance')}
          </StatusChip>
          {emp.lateArrivals > 0 && (
            <StatusChip tone="warning">
              {t('payrollReports.lateBadge', { value: formatCountWithMinutes(emp.lateArrivals, emp.lateMinutes, lang) })}
            </StatusChip>
          )}
          {undecidedHours(emp) > 0 && (
            <StatusChip tone="warning">
              {t('payrollReports.pendingBadge', { value: formatHours(undecidedHours(emp)) })}
            </StatusChip>
          )}
          {paidDiffers && (
            <Tooltip content={t('payrollReports.paidVsEstimate', {
              estimate: formatMoney(emp.overtimeCost, emp.currencyCode, lang),
            })}>
              <StatusChip tone="info">
                {t('payrollReports.paidBadge', { value: formatMoney(paid, emp.currencyCode, lang) })}
              </StatusChip>
            </Tooltip>
          )}
        </>
      ),
      // `rows` is the listing the switch filters; `totalRows` is the month, which the switch must
      // never move — otherwise the subtotal would claim the employee worked 12 hours in August.
      rows: onlyOvertimeDays ? rows.filter(dayHasOvertime) : rows,
      totalRows: rows,
    };
  }), [attendanceData, onlyOvertimeDays, deptName, lang, t]);

  // Collapse state lives with the PARENT so the toolbar's toggle and the table's chevrons drive the
  // same state. One per tab: collapsing August on the summary must not fold a department open on
  // the register.
  const summaryCollapse = useGroupCollapse(summaryGroups, false);
  const registerCollapse = useGroupCollapse(registerGroups, false);
  const departmentCollapse = useGroupCollapse(departmentGroups, false);
  const attendanceCollapse = useGroupCollapse(attendanceGroups, false);

  const collapseByTab: Record<ReportTab, GroupCollapseApi> = {
    summary: summaryCollapse,
    register: registerCollapse,
    department: departmentCollapse,
    attendance: attendanceCollapse,
  };
  const groupCountByTab: Record<ReportTab, number> = {
    summary: summaryGroups.length,
    register: registerGroups.length,
    department: departmentGroups.length,
    attendance: attendanceGroups.length,
  };
  const activeCollapse = collapseByTab[activeTab];

  // ── summary strips ────────────────────────────────────────────────────────────────────────────

  const summaryStrip = useMemo<ReportSummaryItem[]>(() => {
    if (summaryData.length === 0) return [];
    const months = monthsOf(summaryData);
    const latest = months[months.length - 1] ?? '';
    const latestRows = summaryData.filter((r) => r.month === latest);
    return [
      {
        label: t('payrollReports.months'), value: formatInt(months.length, lang),
        icon: CalendarClock, testId: 'summary-months',
      },
      {
        label: t('reports.departments'),
        value: formatInt(new Set(summaryData.map((r) => r.departmentId)).size, lang),
        icon: Building2, testId: 'summary-departments',
      },
      {
        // Headcount is a snapshot, not a running total: adding August's 12 to July's 11 would claim
        // 23 people. The strip therefore reports the LATEST month and says which one it is.
        label: t('payrollReports.headcount'),
        value: formatInt(latestRows.reduce((a, r) => a + r.headcount, 0), lang),
        hint: months.length > 1 ? t('payrollReports.asOfMonth', { month: formatMonth(latest, lang) }) : undefined,
        icon: Users, testId: 'summary-headcount',
      },
      ...currencyItems({
        rows: summaryData, pick: (r) => r.grossTotal, lang,
        label: t('payrollReports.grossTotal'),
        labelIn: (currency) => t('payrollReports.grossTotalIn', { currency }),
        icon: Coins, testIdBase: 'summary-gross',
      }),
      ...currencyItems({
        rows: summaryData, pick: (r) => r.netTotal, lang,
        label: t('payrollReports.netTotal'),
        labelIn: (currency) => t('payrollReports.netTotalIn', { currency }),
        icon: Wallet, testIdBase: 'summary-net',
        decorate: (currency) => {
          const mom = monthOverMonth(summaryData, (r) => r.netTotal, currency);
          if (!mom) return {};
          return {
            hint: t('payrollReports.vsPreviousMonth', {
              delta: `${mom.delta >= 0 ? '+' : ''}${formatPercent(mom.delta, 1, lang)}`,
              month: formatMonth(mom.previousMonth, lang),
            }),
            tone: mom.delta >= 0 ? 'positive' : 'warning',
          };
        },
      }),
    ];
  }, [summaryData, lang, t]);

  const registerStrip = useMemo<ReportSummaryItem[]>(() => {
    if (registerData.length === 0) return [];
    return [
      {
        label: t('payrollReports.employees'),
        value: formatInt(new Set(registerData.map((r) => r.employeeId)).size, lang),
        icon: Users, testId: 'summary-employees',
      },
      {
        // One payslip per employee-month, so this exceeds the employee count on a multi-month run —
        // which is exactly the difference the two lines exist to show.
        label: t('payrollReports.payslips'), value: formatInt(registerData.length, lang),
        icon: Receipt, testId: 'summary-payslips',
      },
      ...currencyItems({
        rows: registerData, pick: (r) => r.grossSalary, lang,
        label: t('payrollReports.grossTotal'),
        labelIn: (currency) => t('payrollReports.grossTotalIn', { currency }),
        icon: Coins, testIdBase: 'summary-gross',
      }),
      ...currencyItems({
        rows: registerData, pick: (r) => r.netPay, lang,
        label: t('payrollReports.netTotal'),
        labelIn: (currency) => t('payrollReports.netTotalIn', { currency }),
        icon: Wallet, testIdBase: 'summary-net',
      }),
      ...currencyItems({
        rows: registerData, pick: (r) => r.overtimePay ?? 0, lang,
        label: t('payrollReports.overtimePay'),
        labelIn: (currency) => t('payrollReports.overtimePayIn', { currency }),
        icon: Clock, testIdBase: 'summary-ot-pay',
      }),
    ];
  }, [registerData, lang, t]);

  const departmentStrip = useMemo<ReportSummaryItem[]>(() => {
    if (departmentData.length === 0) return [];
    const months = monthsOf(departmentData);
    const latest = months[months.length - 1] ?? '';
    const latestRows = departmentData.filter((r) => r.month === latest);
    const asOf = months.length > 1
      ? t('payrollReports.asOfMonth', { month: formatMonth(latest, lang) })
      : undefined;

    // Average cost is re-derived from the latest month's totals rather than averaged out of the
    // per-department averages: the mean of a set of means is not the mean unless every department
    // happens to be the same size.
    const costByCurrency = sumByCurrency(latestRows, (r) => r.totalCost);
    const headByCurrency = sumByCurrency(latestRows, (r) => r.headcount);
    const avgCodes = Array.from(costByCurrency.keys()).sort();

    return [
      {
        label: t('reports.departments'),
        value: formatInt(new Set(departmentData.map((r) => r.departmentId)).size, lang),
        icon: Building2, testId: 'summary-departments',
      },
      {
        label: t('payrollReports.headcount'),
        value: formatInt(latestRows.reduce((a, r) => a + r.headcount, 0), lang),
        hint: asOf, icon: Users, testId: 'summary-headcount',
      },
      ...currencyItems({
        rows: departmentData, pick: (r) => r.totalCost, lang,
        label: t('payrollReports.costToCompany'),
        labelIn: (currency) => t('payrollReports.costToCompanyIn', { currency }),
        icon: Coins, testIdBase: 'summary-cost',
        decorate: (currency) => {
          const mom = monthOverMonth(departmentData, (r) => r.totalCost, currency);
          if (!mom) return {};
          return {
            hint: t('payrollReports.vsPreviousMonth', {
              delta: `${mom.delta >= 0 ? '+' : ''}${formatPercent(mom.delta, 1, lang)}`,
              month: formatMonth(mom.previousMonth, lang),
            }),
            tone: mom.delta >= 0 ? 'positive' : 'warning',
          };
        },
      }),
      ...currencyItems({
        rows: departmentData, pick: (r) => r.employerContributions, lang,
        label: t('payrollReports.employerContributions'),
        labelIn: (currency) => t('payrollReports.employerContributionsIn', { currency }),
        icon: Landmark, testIdBase: 'summary-employer',
      }),
      ...avgCodes.map((code, index) => {
        const head = headByCurrency.get(code) ?? 0;
        return {
          label: avgCodes.length > 1 && code
            ? t('payrollReports.avgCostPerEmployeeIn', { currency: code })
            : t('payrollReports.avgCostPerEmployee'),
          value: formatMoney(head > 0 ? (costByCurrency.get(code) ?? 0) / head : null, code, lang),
          hint: asOf,
          icon: TrendingUp,
          testId: index === 0 ? 'summary-avg-cost' : `summary-avg-cost-${code}`,
        };
      }),
    ];
  }, [departmentData, lang, t]);

  const attendanceStrip = useMemo<ReportSummaryItem[]>(() => {
    if (attendanceData.length === 0) return [];
    const employees = new Set(attendanceData.map((r) => r.employeeId)).size;
    const daysWithOvertime = attendanceData.reduce((a, r) => a + r.daysWithOvertime, 0);
    const regular = attendanceData.reduce((a, r) => a + r.regularOvertimeHours, 0);
    const premium = attendanceData.reduce((a, r) => a + r.premiumOvertimeHours, 0);
    // "Awaiting approval" on the strip counts the hours whose day chip says so (Pending / Mixed),
    // which is what HR reads it against. The CO-304 gate's WITHHELD hours (server
    // pendingApprovalHours) are a different, usually smaller, figure: shown as the hint when > 0.
    const pending = attendanceData.reduce((a, r) => a + undecidedHours(r), 0);
    const withheld = attendanceData.reduce((a, r) => a + r.pendingApprovalHours, 0);

    return [
      { label: t('payrollReports.employees'), value: formatInt(employees, lang), icon: Users, testId: 'summary-employees' },
      {
        label: t('payrollReports.daysWithOvertime'),
        value: formatInt(daysWithOvertime, lang),
        icon: ListChecks,
        testId: 'summary-days-with-overtime',
      },
      {
        label: t('payrollReports.overtimeHoursTotal'),
        value: formatHours(regular + premium),
        hint: t('payrollReports.otHoursHint', { regular: formatHours(regular), premium: formatHours(premium) }),
        icon: Clock,
        testId: 'summary-ot-hours',
      },
      // The first line always answers "what did the overtime cost"; a mixed-currency tenant's extra
      // lines are named by their code so a test can still address one of them.
      ...currencyItems({
        rows: attendanceData, pick: (r) => r.overtimeCost, lang,
        label: t('payrollReports.otCost'),
        labelIn: (currency) => t('payrollReports.otCostIn', { currency }),
        icon: Coins, testIdBase: 'summary-overtime-cost',
      }),
      {
        label: t('payrollReports.pendingApprovalHours'),
        value: formatHours(pending),
        hint: withheld > 0 ? t('payrollReports.withheldHint', { value: formatHours(withheld) }) : undefined,
        tone: pending > 0 ? 'warning' : 'default',
        icon: Hourglass,
        testId: 'summary-pending-hours',
      },
    ];
  }, [attendanceData, lang, t]);

  // ── notes above the table ─────────────────────────────────────────────────────────────────────

  const currentRowCount =
    activeTab === 'summary' ? summaryData.length
      : activeTab === 'register' ? registerData.length
        : activeTab === 'department' ? departmentData.length
          : attendanceData.length;

  const currencySet = useMemo(() => {
    const codes = activeTab === 'summary' ? summaryData.map((r) => r.currencyCode)
      : activeTab === 'register' ? registerData.map((r) => r.currencyCode)
        : activeTab === 'department' ? departmentData.map((r) => r.currencyCode)
          : attendanceData.map((r) => r.currencyCode);
    return new Set(codes.filter((c) => !!c));
  }, [activeTab, summaryData, registerData, departmentData, attendanceData]);

  const mixedCurrencies = currencySet.size > 1;
  const legacyRows = activeTab === 'summary' ? summaryData.reduce((a, r) => a + (r.legacyOvertimeRows || 0), 0) : 0;
  const attendanceOnlyRows = activeTab === 'attendance' ? attendanceData.filter((r) => !r.hasPayrollRow).length : 0;

  // ── exports ───────────────────────────────────────────────────────────────────────────────────

  /**
   * Every tab exports the same way.
   *
   * The client does NOT render the file. It posts the SAME filter the screen ran plus a description
   * of the columns it is showing, and the server re-runs the query and renders the document — so the
   * file and the screen can never disagree about a figure, and the three formats come from one
   * pipeline instead of an in-browser ExcelJS workbook and an html2canvas screenshot that agreed
   * with neither.
   */
  const runExport = useCallback(<Row,>(
    format: ReportExportFormat,
    endpoint: string,
    title: string,
    columns: ReportColumn<Row>[],
    lookups?: Record<string, Record<string, string>>,
  ) => {
    const req = buildExportRequest<PayrollReportInput, Row>({
      input: buildInput(),
      format,
      title,
      columns,
      culture: lang,
      rtl: i18n.dir() === 'rtl',
      labels: { ...reportExportLabels(t), period: periodText, scope: scopeText },
      lookups,
    });
    void exportReport(
      format,
      () => postReportExport(endpoint, req),
      reportExportFilename(title, periodText, format),
    );
  }, [buildInput, lang, i18n, t, periodText, scopeText, exportReport]);

  /** `yyyy-MM` -> `Aug 2026`, so the document prints months in the reader's language rather than
   *  the raw key the query grouped by. Keyed by the `month` column, which is also the group key. */
  const monthLookup = useCallback((months: string[]) => ({
    month: Object.fromEntries(months.map((m) => [m, formatMonth(m, lang)])),
  }), [lang]);

  /** The server stores an employee with no department under the raw name `Unassigned`; on screen
   *  `deptName` swaps it for the translated word, and without this the file kept the English one.
   *  Keyed by the `department` column, which the register export also uses as its group title. */
  const departmentLookup = useMemo(() => ({
    department: { [UNASSIGNED_DEPARTMENT]: unassignedLabel },
  }), [unassignedLabel]);

  const onExport = (format: ReportExportFormat) => {
    if (activeTab === 'summary') {
      runExport(format, 'PayrollReports/Export/PayrollSummary', t('payrollReports.summary'),
        summaryColumns, { ...monthLookup(monthsOf(summaryData)), ...departmentLookup });
      return;
    }
    if (activeTab === 'register') {
      runExport(format, 'PayrollReports/Export/SalaryRegister', t('payrollReports.salaryRegister'),
        registerColumns, { ...monthLookup(monthsOf(registerData)), ...departmentLookup });
      return;
    }
    if (activeTab === 'department') {
      runExport(format, 'PayrollReports/Export/DepartmentCost', t('payrollReports.departmentCost'),
        departmentColumns, { ...monthLookup(monthsOf(departmentData)), ...departmentLookup });
      return;
    }
    runExport(format, 'PayrollReports/Export/AttendanceOvertime', t('payrollReports.attendanceOvertime'),
      attendanceColumns, {
        ...departmentLookup,
        status: Object.fromEntries(DAY_STATUSES.map((s) => [s, t(`reports.dayStatus.${s}`)])),
        approval: Object.fromEntries(OT_APPROVAL_STATUSES.map((s) => [s, t(`reports.otStatus.${s}`)])),
      });
  };

  // ── rendering ─────────────────────────────────────────────────────────────────────────────────

  const filterChips = hasLoaded
    ? [
      { key: 'period', label: periodText, tone: 'accent' as const },
      ...(selectedDepartmentIds.length === 0
        ? [{ key: 'scope', label: scopeText }]
        : selectedDepartmentIds.map((id) => ({
          key: `dept-${id}`,
          label: id === 0 ? unassignedLabel : departments.find((d) => d.id === id)?.name ?? String(id),
          onRemove: () => setSelectedDepartmentIds((prev) => prev.filter((x) => x !== id)),
        }))),
    ]
    : [];

  const emptyState = <ReportEmpty period={periodText} scopeText={scopeText} />;

  return (
    <PageLayout
      title={t('payrollReports.title')}
      description={t('payrollReports.subtitle')}
      icon={BarChart3}
      accentColor="emerald"
      maxWidth="1600px"
    >
      <div className="space-y-6">
        {/* Tabs */}
        <div className="flex flex-wrap gap-1 bg-muted p-1 rounded-lg w-fit" role="tablist">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={activeTab === key}
              onClick={() => switchTab(key)}
              data-testid={`tab-${key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === key
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* One filter bar for all four tabs */}
        <ReportFilterBar
          testId="payroll-reports-filters"
          chips={filterChips}
          actions={
            <>
              <RunButton
                testId="generate-report"
                label={t('payrollReports.generate')}
                loading={loading}
                disabled={loading || invalidRange}
                onClick={() => void fetchTab(activeTab)}
              />
              <ExportMenu
                onExport={onExport}
                busy={exporting}
                disabled={!hasLoaded || currentRowCount === 0}
              />
            </>
          }
        >
          <ReportFilterField label={t('payrollReports.monthRange')}>
            <PeriodPicker mode="month" value={period} onChange={setPeriod} disabled={loading} />
          </ReportFilterField>

          <ReportFilterField label={t('payrollReports.filterByDepartment')} className="max-w-3xl">
            <DepartmentFilter
              departments={departments}
              value={selectedDepartmentIds}
              onChange={setSelectedDepartmentIds}
              unassignedLabel={unassignedLabel}
              disabled={loading}
              testId="department-filter"
            />
          </ReportFilterField>
        </ReportFilterBar>

        {/* Content */}
        {loading ? (
          <ReportLoading columns={SKELETON_COLUMNS[activeTab]} />
        ) : !hasLoaded ? (
          <ReportIdle hint={t('payrollReports.selectAndGenerate')} />
        ) : (
          <div className="space-y-4">
            {(mixedCurrencies || legacyRows > 0 || attendanceOnlyRows > 0) && (
              <div className="space-y-2">
                {mixedCurrencies && (
                  <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning-strong" role="status">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                    <span>{t('payrollReports.mixedCurrencies', { currencies: Array.from(currencySet).sort().join(', ') })}</span>
                  </div>
                )}
                {legacyRows > 0 && (
                  <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground-strong" role="status">
                    <Info className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                    <span>{t('payrollReports.legacyOvertimeNote', { count: legacyRows })}</span>
                  </div>
                )}
                {attendanceOnlyRows > 0 && (
                  <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground-strong" role="status">
                    <Info className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                    <span>{t('payrollReports.attendanceOnlyNote', { count: attendanceOnlyRows })}</span>
                  </div>
                )}
              </div>
            )}

            <ReportSummaryStrip
              items={
                activeTab === 'summary' ? summaryStrip
                  : activeTab === 'register' ? registerStrip
                    : activeTab === 'department' ? departmentStrip
                      : attendanceStrip
              }
              testId={activeTab === 'attendance' ? 'attendance-summary' : `summary-${activeTab}`}
              ariaLabel={tabs.find((x) => x.key === activeTab)?.label}
            />

            <div className="flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => (activeCollapse.allCollapsed ? activeCollapse.expandAll() : activeCollapse.collapseAll())}
                data-testid={`${activeTab}-collapse-toggle`}
                disabled={groupCountByTab[activeTab] === 0}
                className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground-strong transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
              >
                {activeCollapse.allCollapsed ? t('reports.expandAll') : t('reports.collapseAll')}
              </button>

              {activeTab === 'attendance' && (
                <Switch
                  data-testid="only-overtime-toggle"
                  checked={onlyOvertimeDays}
                  onCheckedChange={setOnlyOvertimeDays}
                  label={t('payrollReports.onlyOvertimeDays')}
                  description={t('payrollReports.onlyOvertimeDaysHint')}
                />
              )}
            </div>

            {activeTab === 'summary' ? (
              <ReportTable
                columns={summaryColumns}
                groups={summaryGroups}
                rowKey={(r) => `${r.departmentId}|${r.month}|${r.currencyCode}`}
                currencyOf={(r) => r.currencyCode || undefined}
                collapse={summaryCollapse}
                emptyState={emptyState}
                testId="summary-table"
                ariaLabel={t('payrollReports.summary')}
              />
            ) : activeTab === 'register' ? (
              <ReportTable
                columns={registerColumns}
                groups={registerGroups}
                rowKey={(r) => `${r.employeeId}|${r.month}|${r.currencyCode}`}
                currencyOf={(r) => r.currencyCode || undefined}
                collapse={registerCollapse}
                emptyState={emptyState}
                testId="register-table"
                ariaLabel={t('payrollReports.salaryRegister')}
              />
            ) : activeTab === 'department' ? (
              <ReportTable
                columns={departmentColumns}
                groups={departmentGroups}
                rowKey={(r) => `${r.departmentId}|${r.month}|${r.currencyCode}`}
                currencyOf={(r) => r.currencyCode || undefined}
                collapse={departmentCollapse}
                emptyState={emptyState}
                testId="department-table"
                ariaLabel={t('payrollReports.departmentCost')}
              />
            ) : (
              <ReportTable
                columns={attendanceColumns}
                groups={attendanceGroups}
                rowKey={(r) => r.rowId}
                currencyOf={(r) => r.currencyCode || undefined}
                collapse={attendanceCollapse}
                density="compact"
                rowClassName={(r) => (MUTED_DAY_STATUSES.has(r.dayStatus) ? 'text-muted-foreground' : undefined)}
                emptyState={emptyState}
                testId="attendance-table"
                ariaLabel={t('payrollReports.attendanceOvertime')}
              />
            )}
          </div>
        )}
      </div>
    </PageLayout>
  );
}
